import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { ListToolbar } from '~/components/layout';
import {
  Card,
  CardBody,
  ConfirmModal,
  DataTable,
  RowActionsMenu,
  type DataTableColumn,
} from '~/components/ui';
import { useDeleteEnvironmentMutation } from '~/modules/query/featureFlags';

import { errorText } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { Environment } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

export const EnvironmentsTab = ({ reference }: { reference: Reference }) => {
  const { environments } = reference;
  const [deleting, setDeleting] = useState<Environment | null>(null);
  const [deleteEnvironment] = useDeleteEnvironmentMutation();

  const remove = (env: Environment) => {
    deleteEnvironment({ key: env.key, data: { expectedName: env.name, confirmation: env.key } })
      .unwrap()
      .then(() => toast.success('Environment deleted'))
      .catch((err) =>
        toast.error(errorText(err, 'The environment could not be deleted. Try again.')),
      );
  };

  const columns: DataTableColumn<Environment>[] = [
    {
      id: 'name',
      header: 'Environment',
      cell: (env) => (
        <span className="font-medium text-gray-800 dark:text-white/90">{env.name}</span>
      ),
    },
    {
      id: 'key',
      header: 'Key',
      cell: (env) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{env.key}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (env: Environment) => (
        <RowActionsMenu
          testId={`feature-flags-environments-actions-${env.key}`}
          items={[
            {
              label: 'Rename',
              testId: 'rename',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.featureFlagsAdminRecord('environments', env.key),
            },
            {
              label: 'Delete',
              testId: 'delete',
              destructive: true,
              disabled: environments.length <= 1,
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              onClick: () => setDeleting(env),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Card>
        <ListToolbar
          title="Environments"
          description="Separate settings for development, testing and production. Keep at least one environment."
        />
        <CardBody className="p-0">
          <DataTable<Environment>
            framed={false}
            testId="feature-flags-environments-table"
            loading={reference.pending && environments.length === 0}
            emptyMessage="No environments yet."
            emptyDescription="Add an environment before creating flags or SDK keys."
            data={environments}
            rowKey={(env) => env.key}
            href={(env) => routes.featureFlagsAdminRecord('environments', env.key)}
            rowLabel={(env) => `Open ${env.name}`}
            columns={columns}
          />
        </CardBody>
      </Card>

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete environment"
        message={`Delete environment '${deleting?.name}'?`}
        confirmLabel="Delete environment"
        variant="danger"
        dangerNote="This permanently removes its flag settings, update settings, saved choices, users and SDK keys. Feature requests are kept. Apps using this environment stop receiving settings."
        confirmText={deleting?.key}
        testId="feature-flags-environments-delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove(deleting);
          setDeleting(null);
        }}
      />
    </>
  );
};

export default EnvironmentsTab;
